import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { PageHeader } from "../components/Common";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

function buildPresets() {
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const Y = now.getFullYear();
  const M = now.getMonth();
  const mStart = (yr, mo) => `${yr}-${pad(mo + 1)}-01`;
  const mEnd = (yr, mo) => { const d = new Date(yr, mo + 1, 0); return `${yr}-${pad(mo + 1)}-${pad(d.getDate())}`; };
  const lm = M === 0 ? { y: Y - 1, m: 11 } : { y: Y, m: M - 1 };
  const q = Math.floor(M / 3) * 3;
  return {
    "this-year": { label: "Bu Yıl", start: `${Y}-01-01`, end: `${Y}-12-31` },
    "last-year": { label: "Geçen Yıl", start: `${Y - 1}-01-01`, end: `${Y - 1}-12-31` },
    "this-quarter": { label: "Bu Çeyrek", start: mStart(Y, q), end: mEnd(Y, q + 2) },
    "this-month": { label: "Bu Ay", start: mStart(Y, M), end: mEnd(Y, M) },
    "last-month": { label: "Geçen Ay", start: mStart(lm.y, lm.m), end: mEnd(lm.y, lm.m) },
  };
}

const METRICS = [
  { k: "income", label: "Toplam Gelir", type: "money", good: "up" },
  { k: "expense", label: "Toplam Gider", type: "money", good: "down" },
  { k: "net", label: "Net Kâr / Zarar", type: "money", good: "up" },
  { k: "profit_margin", label: "Kâr Marjı", type: "percent", good: "up" },
  { k: "collected_vat", label: "Hesaplanan KDV", type: "money", good: "neutral" },
  { k: "paid_vat", label: "İndirilecek KDV", type: "money", good: "neutral" },
  { k: "net_vat", label: "Ödenecek KDV", type: "money", good: "down" },
  { k: "income_count", label: "Gelir İşlem Sayısı", type: "count", good: "up" },
  { k: "expense_count", label: "Gider İşlem Sayısı", type: "count", good: "neutral" },
];

const fmtVal = (v, type) => (type === "money" ? fmtTRY(v) : type === "percent" ? `%${(v || 0).toFixed(1)}` : String(v || 0));

export default function Karsilastirma() {
  const presets = buildPresets();
  const [pa, setPa] = useState("this-year");
  const [pb, setPb] = useState("last-year");
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  useEffect(() => { const p = presets[pa]; api.get(`/period-summary?start=${p.start}&end=${p.end}`).then(({ data }) => setA(data)); /* eslint-disable-next-line */ }, [pa]);
  useEffect(() => { const p = presets[pb]; api.get(`/period-summary?start=${p.start}&end=${p.end}`).then(({ data }) => setB(data)); /* eslint-disable-next-line */ }, [pb]);

  return (
    <div className="p-8 max-w-[1100px]">
      <PageHeader title="Dönem Karşılaştırma" subtitle="İki dönemi yan yana karşılaştırın (gelir, gider, kâr, KDV)" />

      <div className="grid grid-cols-2 gap-4 mb-6 animate-fade-up">
        <PeriodSelect label="Dönem A" value={pa} onChange={setPa} presets={presets} testid="period-a" />
        <PeriodSelect label="Dönem B" value={pb} onChange={setPb} presets={presets} testid="period-b" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="comparison-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Metrik</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">{presets[pa].label} (A)</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">{presets[pb].label} (B)</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Fark (A−B)</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => {
              const av = a ? a[m.k] || 0 : 0;
              const bv = b ? b[m.k] || 0 : 0;
              const delta = av - bv;
              const pct = bv ? (delta / Math.abs(bv)) * 100 : null;
              const up = delta > 0;
              const isGood = m.good === "neutral" ? null : (m.good === "up" ? up : !up);
              const color = delta === 0 || isGood === null ? "text-zinc-500" : isGood ? "text-green-600" : "text-red-600";
              const Icon = delta === 0 ? Minus : up ? TrendUp : TrendDown;
              return (
                <tr key={m.k} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`cmp-row-${m.k}`}>
                  <td className="px-4 py-3 font-medium text-zinc-800">{m.label}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">{fmtVal(av, m.type)}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500 tabular-nums">{fmtVal(bv, m.type)}</td>
                  <td className={`px-4 py-3 text-right font-mono tabular-nums ${color}`}>
                    <div className="flex items-center justify-end gap-1.5">
                      <Icon size={13} weight="bold" />
                      <span>{m.type === "count" ? (delta > 0 ? `+${delta}` : delta) : (delta >= 0 ? "+" : "") + fmtVal(delta, m.type)}</span>
                      {pct !== null && <span className="text-[11px] opacity-70">({pct >= 0 ? "+" : ""}{pct.toFixed(0)}%)</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PeriodSelect({ label, value, onChange, presets, testid }) {
  const p = presets[value];
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="inp">
        {Object.entries(presets).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <div className="text-[11px] text-zinc-400 mt-2 font-mono">{p.start} → {p.end}</div>
    </div>
  );
}
